<template>
  <Card size="medium" variant="outline">
    <template #header>
      <Text variant="h4">Showing duration</Text>
    </template>

    <Select class="showing-duration-select" v-model="showingDuration" @change="durationChanged">
      <option value="10">10 minutes</option>
      <option value="15">15 minutes</option>
      <option value="30">30 minutes</option>
      <option value="45">45 minutes</option>
      <option value="60">1 hour</option>
    </Select>
  </Card>

  <Card size="medium" variant="outline" v-if="availabilityStore.availabilityLoaded">
    <template #header>
      <Text variant="h4">Weekly schedule</Text>
    </template>

    <Availability
      @slot-added="handleSlotAdded"
      @slot-updated="handleSlotUpdated"
      @slot-deleted="availabilityStore.deleteSlots"
      :availability="availabilityStore.getAvailabilitySlots"
      :investor-availability-id="availabilityStore.getInvestorAvailabilityId"
      ref="availabilityRef"
    />
  </Card>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { ref, watch } from 'vue';

import { Card, Select, Text } from '@tallo/design-system';
import { useAvailabilityStore, AvailabilitySlot, NewAvailabilitySlotDto } from '@tallo/investor/availability';

import Availability from './availability.vue';

const availabilityStore = useAvailabilityStore();
const availabilityRef = ref<InstanceType<typeof Availability> | null>(null);

const showingDuration = ref<number | null>(null);
let previousShowingDuration = ref<number | null>(null);

// Handle slot added event
const handleSlotAdded = async (newSlot: NewAvailabilitySlotDto) => {
  // Call the store method to add the slot and get the saved slot with ID
  const savedSlot = await availabilityStore.addSlot(newSlot);

  // If we got a saved slot back, update the component
  if (savedSlot && availabilityRef.value) {
    availabilityRef.value.onSlotAdded(newSlot, savedSlot);
  }
};

// Handle slot updated event
const handleSlotUpdated = async (updatedSlot: AvailabilitySlot) => {
  // Call the store method to update the slot and get the saved slot
  const savedSlot = await availabilityStore.updateSlot(updatedSlot);

  // If we got a saved slot back, update the component
  if (savedSlot && availabilityRef.value) {
    availabilityRef.value.onSlotUpdated(savedSlot);
  }
};

const durationChanged = async () => {
  if (showingDuration.value) {
    try {
      await availabilityStore.updateShowingDuration(+showingDuration.value).then(() => {
        previousShowingDuration.value = showingDuration.value!;
      });
    } catch (error) {
      showingDuration.value = previousShowingDuration.value;
    }
  }
};

const { investorAvailability } = storeToRefs(availabilityStore);
watch(
  investorAvailability,
  (investorAvailabilityData) => {
    if (investorAvailabilityData) {
      showingDuration.value = investorAvailabilityData.showingDurationInMinutes;
      previousShowingDuration.value = investorAvailabilityData.showingDurationInMinutes;
    }
  },
  { immediate: true },
);
</script>

<style scoped lang="scss">
.showing-duration-select {
  display: block;
}
</style>
